import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { Card } from "@dl/web-core-js/ui";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

/**
 * The view-code gate, in one place.
 *
 * `RouteAccessChecker` refused the route outright in Vaadin; here the nav
 * already hides the entry, so this covers the pasted-URL case and keeps the
 * wording identical across every screen.
 */
export function useAllowed(view?: string): boolean {
  const { hasView } = useAuth();
  return view ? hasView(view) : true;
}

export function AccessDenied({ title }: { title: string }) {
  const { t } = useTranslation();
  return (
    <>
      <PageHeader title={title} />
      <div className="p-4 lg:p-6">
        <Card className="p-6">
          <p className="text-[13px] text-ink-muted">
            {t(
              "error.access-denied.detail",
              "Your account does not have permission to view this page.",
            )}
          </p>
        </Card>
      </div>
    </>
  );
}

export function ErrorState({ title, error }: { title: string; error: unknown }) {
  const { t } = useTranslation();
  return (
    <>
      <PageHeader title={title} />
      <div className="p-4 lg:p-6">
        <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
          {error instanceof Error
            ? error.message
            : t("error.generic", "Something went wrong. Please try again.")}
        </p>
      </div>
    </>
  );
}

/** Full-height column so a grid inside can own the remaining space. */
export function PageBody({ children }: { children: ReactNode }) {
  return <div className="flex min-h-0 flex-1 flex-col">{children}</div>;
}
